import React, { useCallback, useMemo, useState } from 'react';

export type TopologyFlowId = 'pcie' | 'peer' | 'nic';

/**
 * Data paths aligned with typical PyTorch-style training and NCCL:
 * pinned CPU batches → async H2D DMA over PCIe → forward/backward on GPU →
 * gradient sync via NCCL (intra-node GPU–GPU; inter-node via NIC / GPUDirect when applicable).
 *
 * @see PyTorch pin_memory / non_blocking tutorials; NCCL topology & GDR behavior in NCCL docs.
 */
export const TopologyViz: React.FC = () => {
  const [legendHover, setLegendHover] = useState<TopologyFlowId | null>(null);
  const [diagramHover, setDiagramHover] = useState<TopologyFlowId | null>(null);
  const [pinnedFlow, setPinnedFlow] = useState<TopologyFlowId | null>(null);

  const activeFlow = pinnedFlow ?? legendHover ?? diagramHover;

  const vizClassName = useMemo(() => {
    const base = 'topology-viz';
    if (!activeFlow) return base;
    return `${base} topology-viz--focus-${activeFlow}`;
  }, [activeFlow]);

  const togglePin = useCallback((flow: TopologyFlowId) => {
    setPinnedFlow((p) => (p === flow ? null : flow));
  }, []);

  const clearDiagramHover = useCallback(() => {
    setDiagramHover(null);
  }, []);

  const showAllFlows = useCallback(() => {
    setPinnedFlow(null);
    setLegendHover(null);
    setDiagramHover(null);
  }, []);

  return (
  <div
    className={vizClassName}
    role="region"
    aria-label="Interactive diagram: data paths through host memory, GPUs, and NIC"
  >
    <ul className="topology-viz__blurb">
      <li>Workers stage batches in page-locked CPU memory.</li>
      <li>
        The runtime issues async copies into VRAM over PCIe, often overlapping with the previous batch.
      </li>
      <li>Forward and backward stay on the accelerators.</li>
      <li>
        Distributed jobs add NCCL-style traffic across GPU links and the NIC when training spans nodes.
      </li>
    </ul>

    <p className="topology-viz__legend-note">
      Green tones mark traffic that stays on this node: CPU staging, PCIe DMA into VRAM, and GPU link traffic. Orange marks
      the NIC path when tensors or gradients cross machines.
    </p>

    <div
      className="topology-viz__key"
      role="group"
      aria-label="Path types. Hover or click to highlight a flow. Use Show all flows to clear the selection."
    >
      <button
        type="button"
        className={`topology-viz__key-item topology-viz__key-btn${activeFlow === 'pcie' ? ' topology-viz__key-btn--active' : ''}`}
        onMouseEnter={() => setLegendHover('pcie')}
        onMouseLeave={() => setLegendHover(null)}
        onClick={() => togglePin('pcie')}
        aria-pressed={pinnedFlow === 'pcie'}
      >
        <span className="topology-viz__swatch topology-viz__swatch--pcie" aria-hidden />
        <span>
          <span className="topology-viz__key-title">Host → GPU (input)</span>
          <span className="topology-viz__key-detail">Pinned DRAM, then PCIe DMA into VRAM for each batch</span>
        </span>
      </button>
      <button
        type="button"
        className={`topology-viz__key-item topology-viz__key-btn${activeFlow === 'peer' ? ' topology-viz__key-btn--active' : ''}`}
        onMouseEnter={() => setLegendHover('peer')}
        onMouseLeave={() => setLegendHover(null)}
        onClick={() => togglePin('peer')}
        aria-pressed={pinnedFlow === 'peer'}
      >
        <span className="topology-viz__swatch topology-viz__swatch--peer" aria-hidden />
        <span>
          <span className="topology-viz__key-title">GPU ↔ GPU</span>
          <span className="topology-viz__key-detail">NCCL collectives on fast intra-node links (NVLink-class or PCIe)</span>
        </span>
      </button>
      <button
        type="button"
        className={`topology-viz__key-item topology-viz__key-btn${activeFlow === 'nic' ? ' topology-viz__key-btn--active' : ''}`}
        onMouseEnter={() => setLegendHover('nic')}
        onMouseLeave={() => setLegendHover(null)}
        onClick={() => togglePin('nic')}
        aria-pressed={pinnedFlow === 'nic'}
      >
        <span className="topology-viz__swatch topology-viz__swatch--net" aria-hidden />
        <span>
          <span className="topology-viz__key-title">NIC</span>
          <span className="topology-viz__key-detail">Inter-node gradient chunks when training spans machines</span>
        </span>
      </button>
      <div className="topology-viz__key-reset">
        <button type="button" className="topology-viz__show-all-btn" onClick={showAllFlows}>
          Show all flows
        </button>
      </div>
    </div>

    <svg
      className="topology-viz__svg"
      viewBox="0 0 640 332"
      fill="none"
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      onPointerLeave={clearDiagramHover}
    >
      <defs>
        <linearGradient id="topology-node-surface" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#f4f4f5" />
        </linearGradient>
        <filter id="topology-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.3" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id="topology-pulse-grad" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(15, 118, 110, 0.18)" />
          <stop offset="100%" stopColor="rgba(15, 118, 110, 0)" />
        </radialGradient>
      </defs>

      <g className="topology-viz__grid">
        <line x1="0" y1="96" x2="640" y2="96" />
        <line x1="0" y1="192" x2="640" y2="192" />
        <line x1="160" y1="0" x2="160" y2="332" />
        <line x1="320" y1="0" x2="320" y2="332" />
        <line x1="480" y1="0" x2="480" y2="332" />
      </g>

      <path id="topo-mpath-down-numa0" d="M 280 62 L 280 92 L 128 92 L 128 116" fill="none" stroke="none" />
      <path id="topo-mpath-down-numa1" d="M 280 62 L 280 92 L 432 92 L 432 116" fill="none" stroke="none" />
      <path id="topo-mpath-numa0-gpu" d="M 128 160 L 106 210" fill="none" stroke="none" />
      <path id="topo-mpath-numa1-gpu" d="M 432 160 L 470 210" fill="none" stroke="none" />
      <path id="topo-mpath-host-nic" d="M 280 62 L 280 210" fill="none" stroke="none" />
      <path id="topo-mpath-peer" d="M 118 236 Q 280 176 458 236" fill="none" stroke="none" />

      <g className="topology-viz__tracks">
        <path className="topology-viz__track topology-viz__track--base" d="M 280 62 L 280 92 L 128 92 L 128 116" />
        <path
          className="topology-viz__track topology-viz__track--glow topology-viz__track--pcie"
          d="M 280 62 L 280 92 L 128 92 L 128 116"
        />
        <path className="topology-viz__track topology-viz__track--base" d="M 280 62 L 280 92 L 432 92 L 432 116" />
        <path
          className="topology-viz__track topology-viz__track--glow topology-viz__track--pcie topology-viz__track--glow-delay"
          d="M 280 62 L 280 92 L 432 92 L 432 116"
        />
        <path className="topology-viz__track topology-viz__track--base" d="M 128 160 L 106 210" />
        <path className="topology-viz__track topology-viz__track--glow topology-viz__track--pcie" d="M 128 160 L 106 210" />
        <path className="topology-viz__track topology-viz__track--base" d="M 432 160 L 470 210" />
        <path
          className="topology-viz__track topology-viz__track--glow topology-viz__track--pcie topology-viz__track--glow-delay"
          d="M 432 160 L 470 210"
        />
        <path className="topology-viz__track topology-viz__track--warn" d="M 280 62 L 280 210" />
        <path className="topology-viz__track topology-viz__track--peer" d="M 118 236 Q 280 176 458 236" />
      </g>

      <circle className="topology-viz__pulse" cx="106" cy="236" r="26" fill="url(#topology-pulse-grad)" />
      <circle className="topology-viz__pulse topology-viz__pulse--delay" cx="470" cy="236" r="26" fill="url(#topology-pulse-grad)" />

      <g className="topology-viz__hit-zones" aria-hidden>
        <rect
          className="topology-viz__hit-clear"
          x="0"
          y="0"
          width="640"
          height="332"
          onPointerEnter={clearDiagramHover}
        />
        <path
          className="topology-viz__hit topology-viz__hit--pcie"
          d="M 280 62 L 280 92 L 128 92 L 128 116"
          onPointerEnter={() => setDiagramHover('pcie')}
        />
        <path
          className="topology-viz__hit topology-viz__hit--pcie"
          d="M 280 62 L 280 92 L 432 92 L 432 116"
          onPointerEnter={() => setDiagramHover('pcie')}
        />
        <path
          className="topology-viz__hit topology-viz__hit--pcie"
          d="M 128 160 L 106 210"
          onPointerEnter={() => setDiagramHover('pcie')}
        />
        <path
          className="topology-viz__hit topology-viz__hit--pcie"
          d="M 432 160 L 470 210"
          onPointerEnter={() => setDiagramHover('pcie')}
        />
        <path
          className="topology-viz__hit topology-viz__hit--nic"
          d="M 280 62 L 280 210"
          onPointerEnter={() => setDiagramHover('nic')}
        />
        <path
          className="topology-viz__hit topology-viz__hit--peer"
          d="M 118 236 Q 280 176 458 236"
          onPointerEnter={() => setDiagramHover('peer')}
        />
      </g>

      {/* Host: dataloader lives in process; batches land in pinned pages before H2D */}
      <g className="topology-viz__glyph-host topology-viz__glyph-node">
        <rect
          className="topology-viz__node-surface"
          x="196"
          y="8"
          width="168"
          height="54"
          rx="11"
          fill="url(#topology-node-surface)"
        />
        <text className="topology-viz__glyph-kicker" x="206" y="22">
          Training process
        </text>
        <rect className="topology-viz__glyph-strip topology-viz__glyph-strip--pinned" x="206" y="26" width="148" height="22" rx="4" />
        <text className="topology-viz__glyph-strip-label" x="280" y="41" textAnchor="middle">
          Pinned batch tensors
        </text>
        <rect className="topology-viz__node-outline" x="196" y="8" width="168" height="54" rx="11" />
      </g>

      {/* NUMA domains */}
      <g className="topology-viz__glyph-numa topology-viz__glyph-node">
        <rect
          className="topology-viz__node-surface"
          x="64"
          y="116"
          width="128"
          height="42"
          rx="9"
          fill="url(#topology-node-surface)"
        />
        <text className="topology-viz__hw-title topology-viz__hw-title--sm" x="128" y="130" textAnchor="middle">
          NUMA 0
        </text>
        {[0, 1, 2, 3].map((i) => (
          <rect key={i} className="topology-viz__glyph-dimm" x={76 + i * 24} y="136" width="10" height="16" rx="1" />
        ))}
        <rect className="topology-viz__node-outline topology-viz__node-outline--numa" x="64" y="116" width="128" height="42" rx="9" />
      </g>
      <g className="topology-viz__glyph-numa topology-viz__glyph-node">
        <rect
          className="topology-viz__node-surface"
          x="368"
          y="116"
          width="128"
          height="42"
          rx="9"
          fill="url(#topology-node-surface)"
        />
        <text className="topology-viz__hw-title topology-viz__hw-title--sm" x="432" y="130" textAnchor="middle">
          NUMA 1
        </text>
        {[0, 1, 2, 3].map((i) => (
          <rect key={i} className="topology-viz__glyph-dimm" x={380 + i * 24} y="136" width="10" height="16" rx="1" />
        ))}
        <rect className="topology-viz__node-outline topology-viz__node-outline--numa" x="368" y="116" width="128" height="42" rx="9" />
      </g>

      {/* Accelerators */}
      <g className="topology-viz__glyph-gpu topology-viz__glyph-node">
        <rect
          className="topology-viz__node-surface"
          x="48"
          y="208"
          width="116"
          height="52"
          rx="9"
          fill="url(#topology-node-surface)"
        />
        <path className="topology-viz__glyph-bracket" d="M 54 214 L 54 254" />
        <rect className="topology-viz__glyph-die" x="62" y="218" width="40" height="26" rx="3" />
        <text className="topology-viz__glyph-vram" x="82" y="234" textAnchor="middle">
          VRAM
        </text>
        {[0, 1, 2, 3].map((i) => (
          <line key={i} className="topology-viz__glyph-fin" x1={108 + i * 6} y1="218" x2={108 + i * 6} y2="248" />
        ))}
        <rect className="topology-viz__glyph-pcie" x="58" y="254" width="96" height="3" rx="1" />
        <text className="topology-viz__hw-title topology-viz__hw-title--sm" x="106" y="212" textAnchor="middle">
          GPU 0
        </text>
        <rect className="topology-viz__node-outline topology-viz__node-outline--gpu" x="48" y="208" width="116" height="52" rx="9" />
      </g>
      <g className="topology-viz__glyph-gpu topology-viz__glyph-node">
        <rect
          className="topology-viz__node-surface"
          x="412"
          y="208"
          width="116"
          height="52"
          rx="9"
          fill="url(#topology-node-surface)"
        />
        <path className="topology-viz__glyph-bracket" d="M 418 214 L 418 254" />
        <rect className="topology-viz__glyph-die" x="426" y="218" width="40" height="26" rx="3" />
        <text className="topology-viz__glyph-vram" x="446" y="234" textAnchor="middle">
          VRAM
        </text>
        {[0, 1, 2, 3].map((i) => (
          <line key={i} className="topology-viz__glyph-fin" x1={472 + i * 6} y1="218" x2={472 + i * 6} y2="248" />
        ))}
        <rect className="topology-viz__glyph-pcie" x="422" y="254" width="96" height="3" rx="1" />
        <text className="topology-viz__hw-title topology-viz__hw-title--sm" x="470" y="212" textAnchor="middle">
          GPU 1
        </text>
        <rect className="topology-viz__node-outline topology-viz__node-outline--gpu" x="412" y="208" width="116" height="52" rx="9" />
      </g>

      <g className="topology-viz__glyph-nic topology-viz__glyph-node">
        <rect
          className="topology-viz__node-surface"
          x="244"
          y="208"
          width="104"
          height="52"
          rx="9"
          fill="url(#topology-node-surface)"
        />
        <rect className="topology-viz__glyph-nic-body" x="252" y="218" width="40" height="32" rx="3" />
        <rect className="topology-viz__glyph-port" x="298" y="222" width="14" height="10" rx="2" />
        <rect className="topology-viz__glyph-port" x="298" y="236" width="14" height="10" rx="2" />
        <line className="topology-viz__glyph-line" x1="312" y1="227" x2="324" y2="227" />
        <line className="topology-viz__glyph-line" x1="312" y1="241" x2="324" y2="241" />
        <text className="topology-viz__hw-title topology-viz__hw-title--sm" x="296" y="212" textAnchor="middle">
          NIC
        </text>
        <rect className="topology-viz__node-outline topology-viz__node-outline--nic" x="244" y="208" width="104" height="52" rx="9" />
      </g>

      <g className="topology-viz__annotations">
        <text className="topology-viz__edge-cap" x="286" y="78">
          workers · cgroup
        </text>
        <text className="topology-viz__edge-cap" x="128" y="108" textAnchor="middle">
          collated batch
        </text>
        <text className="topology-viz__edge-cap" x="432" y="108" textAnchor="middle">
          collated batch
        </text>
        <text className="topology-viz__edge-cap" x="118" y="182" textAnchor="middle">
          async H2D
        </text>
        <text className="topology-viz__edge-cap" x="452" y="182" textAnchor="middle">
          async H2D
        </text>
        <text className="topology-viz__edge-cap" x="280" y="100" textAnchor="middle">
          inter-node fabric
        </text>
        <text className="topology-viz__edge-cap" x="280" y="112" textAnchor="middle">
          (GDR when path OK)
        </text>
        <text className="topology-viz__edge-cap topology-viz__edge-cap--peer" x="280" y="164" textAnchor="middle">
          AllReduce · intra-node
        </text>
      </g>

      <g className="topology-viz__packets" filter="url(#topology-glow)">
        <g className="topology-viz__packet-group topology-viz__packet-group--pcie">
        <circle className="topology-viz__packet" r="5" fill="#166534">
          <animateMotion dur="2.4s" repeatCount="indefinite" rotate="auto" calcMode="linear">
            <mpath xlinkHref="#topo-mpath-down-numa0" />
          </animateMotion>
        </circle>
        <circle className="topology-viz__packet topology-viz__packet--lag" r="4.5" fill="#15803d">
          <animateMotion dur="2.4s" repeatCount="indefinite" rotate="auto" begin="1.2s" calcMode="linear">
            <mpath xlinkHref="#topo-mpath-down-numa0" />
          </animateMotion>
        </circle>
        <circle className="topology-viz__packet" r="5" fill="#166534">
          <animateMotion dur="2.4s" repeatCount="indefinite" rotate="auto" begin="-0.55s" calcMode="linear">
            <mpath xlinkHref="#topo-mpath-down-numa1" />
          </animateMotion>
        </circle>
        <circle className="topology-viz__packet" r="4.5" fill="#15803d">
          <animateMotion dur="1.55s" repeatCount="indefinite" rotate="auto" calcMode="linear">
            <mpath xlinkHref="#topo-mpath-numa0-gpu" />
          </animateMotion>
        </circle>
        <circle className="topology-viz__packet topology-viz__packet--lag" r="4" fill="#22c55e">
          <animateMotion dur="1.55s" repeatCount="indefinite" rotate="auto" begin="0.75s" calcMode="linear">
            <mpath xlinkHref="#topo-mpath-numa0-gpu" />
          </animateMotion>
        </circle>
        <circle className="topology-viz__packet" r="4.5" fill="#15803d">
          <animateMotion dur="1.55s" repeatCount="indefinite" rotate="auto" begin="-0.3s" calcMode="linear">
            <mpath xlinkHref="#topo-mpath-numa1-gpu" />
          </animateMotion>
        </circle>
        </g>
        <g className="topology-viz__packet-group topology-viz__packet-group--nic">
        <circle className="topology-viz__packet topology-viz__packet--warn" r="5" fill="#d97706">
          <animateMotion dur="2.75s" repeatCount="indefinite" rotate="auto" calcMode="linear">
            <mpath xlinkHref="#topo-mpath-host-nic" />
          </animateMotion>
        </circle>
        </g>
        <g className="topology-viz__packet-group topology-viz__packet-group--peer">
        <circle className="topology-viz__packet topology-viz__packet--peer" r="3.8" fill="#14b8a6">
          <animateMotion dur="2.1s" repeatCount="indefinite" rotate="auto" calcMode="linear">
            <mpath xlinkHref="#topo-mpath-peer" />
          </animateMotion>
        </circle>
        <circle className="topology-viz__packet topology-viz__packet--peer" r="3.3" fill="#5eead4">
          <animateMotion dur="2.1s" repeatCount="indefinite" rotate="auto" begin="1.05s" calcMode="linear">
            <mpath xlinkHref="#topo-mpath-peer" />
          </animateMotion>
        </circle>
        </g>
      </g>
    </svg>

    <p className="topology-viz__caption">
      Dark green motion is host staging and H2D into VRAM; the lighter teal arc is NCCL-style GPU to GPU traffic on-node.
      Real frameworks overlap transfers and compute across micro-batches. Hover host, NUMA, GPU, or NIC boxes to enlarge them;
      use the legend or paths to isolate a flow.
    </p>
  </div>
  );
};
